import { Mark, mergeAttributes } from '@tiptap/core';

/**
 * A collaborative comment-anchor mark. It carries `threadId` (the DB thread id) and renders the marked
 * range with `.wiki-comment-mark[data-thread-id]`. The mark lives in the Yjs doc, so the anchored range
 * survives concurrent edits; the DB thread references the same id. Active/resolved *styling* is applied
 * by a decoration in wiki-editor (not the mark), so this mark just paints the base highlight.
 */
export interface CommentMarkOptions {
  onActivate: (threadId: string) => void;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    wikiComment: {
      setCommentThread: (threadId: string) => ReturnType;
      unsetCommentThread: (threadId: string) => ReturnType;
    };
  }
}

export const WikiCommentMark = Mark.create<CommentMarkOptions>({
  name: 'commentThread',
  inclusive: false, // typing at the mark's edge does NOT extend the highlight (like Google Docs)
  excludes: '', // allow overlap with other marks (bold/link/etc.)
  addOptions() {
    return { onActivate: () => {} };
  },
  addAttributes() {
    return {
      threadId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-thread-id'),
        renderHTML: (a) => (a.threadId ? { 'data-thread-id': a.threadId } : {}),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-thread-id]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'wiki-comment-mark' }), 0];
  },
  addCommands() {
    return {
      // Apply to the CURRENT selection (must be a non-empty text selection).
      setCommentThread:
        (threadId) =>
        ({ commands }) =>
          commands.setMark(this.name, { threadId }),
      // Remove every commentThread mark carrying this id across the doc (resolve-delete / cancel).
      unsetCommentThread:
        (threadId) =>
        ({ state, dispatch }) => {
          const { doc, tr } = state;
          const type = state.schema.marks.commentThread;
          let found = false;
          doc.descendants((node, pos) => {
            if (!node.isText) return;
            node.marks.forEach((mk) => {
              if (mk.type === type && mk.attrs.threadId === threadId) {
                tr.removeMark(pos, pos + node.nodeSize, type);
                found = true;
              }
            });
          });
          if (found && dispatch) dispatch(tr);
          return found;
        },
    };
  },
});
